// src/components/ConditionsPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { clickable } from "../utils/a11y";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_DETAILS } from "../App";
import "../styles/Home.css";
import { conditionsData } from "./conditionsData";

// Build cards from the real data source so we have `id`
const CONDITIONS = conditionsData.map((c) => ({
  ...c,
  // keep existing UI fields that your grid expects:
  percentage: c.stat || "",
  badge: c.category,
  description: c.shortDescription || c.longDescription || "",
}));

const FILTER_TAGS = [
  "All",
  "Prostate Health",
  "Bladder Health",
  "Sexual Health",
  "Urinary Health",
  "Kidney Health",
  "Hormonal Health",
];

const SearchIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.3-4.3" />
  </svg>
);

const PeopleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const ConditionsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchText, setSearchText] = useState("");
  const [activeFilter, setActiveFilter] = useState("All");
  // null = no active keyword-search filter (show all, subject to category filter)
  const [matchedNames, setMatchedNames] = useState(null);
  const searchRequestId = useRef(0);

  const selectedConditionName =
    location.state && location.state.selectedConditionName;

  // Debounced call to the backend condition-search endpoint, which matches
  // on name + category + clinical searchKeywords (e.g. "bph" -> Enlarged Prostate).
  useEffect(() => {
    const term = searchText.trim();
    if (!term) {
      setMatchedNames(null);
      return;
    }

    const requestId = ++searchRequestId.current;
    const timer = setTimeout(async () => {
      try {
        const url =
          API_DETAILS.baseUrl +
          API_DETAILS.PORT +
          API_DETAILS.baseExtension +
          `/searchConditions?term=${encodeURIComponent(term)}`;
        const res = await axios.get(url);
        if (requestId === searchRequestId.current) {
          setMatchedNames((res.data || []).map((c) => c.name));
        }
      } catch (err) {
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [searchText]);

  const filteredConditions = useMemo(() => {
    return CONDITIONS.filter((c) => {
      const matchesFilter =
        activeFilter === "All" || c.category === activeFilter;
      const matchesSearch =
        !searchText.trim() ||
        matchedNames === null ||
        matchedNames.includes(c.name);
      return matchesFilter && matchesSearch;
    });
  }, [activeFilter, searchText, matchedNames]);

  return (
    <div className="conditions-page-root">
      {/* Top nav (same style as Home) */}
      <header className="home-nav conditions-nav">
        <Link to="/" className="home-nav-left" aria-label="Youro home">
          <div className="home-nav-logo-circle">Y</div>
          <span className="home-nav-logo-text">Youro</span>
        </Link>
        <nav className="home-nav-center">
          <button
            className="home-nav-link"
            onClick={() => navigate("/about")}
          >
            About
          </button>
          <button
            className="home-nav-link home-nav-link-active"
          >
            Find Your Diagnosis
          </button>
          <button
            className="home-nav-link"
            onClick={() => navigate("/assessment")}
          >
            Symptom Assessment
          </button>
        </nav>
        <div className="home-nav-right">
          <button
            className="home-nav-signin"
            onClick={() => navigate("/login")}
          >
            Sign In
          </button>
          <button
            className="home-nav-get-started"
            onClick={() => navigate("/signup")}
          >
            Get Started
          </button>
        </div>
      </header>

      <main className="conditions-main">
        <button
          className="conditions-back-link"
          onClick={() => navigate("/")}
        >
          ← Back to Home
        </button>

        <section className="conditions-hero">
          <h1 className="conditions-title">
            Expert urologic care for your condition
          </h1>
          <p className="conditions-subtitle">
            Explore our specialized urologic treatment areas and learn how our
            expert providers can help you achieve optimal urologic health.
          </p>

          {/* Search bar */}
          <div className="conditions-search-wrapper">
            <span className="conditions-search-icon">
              <SearchIcon />
            </span>
            <input
              className="conditions-search-input"
              placeholder="Search conditions…"
              aria-label="Search conditions"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>

          {/* Filter chips */}
          <div className="conditions-filter-row">
            {FILTER_TAGS.map((tag) => (
              <button
                key={tag}
                className={
                  "conditions-filter-chip" +
                  (activeFilter === tag
                    ? " conditions-filter-chip-active"
                    : "")
                }
                onClick={() => setActiveFilter(tag)}
              >
                {tag}
              </button>
            ))}
          </div>
        </section>

        {/* CONDITIONS GRID */}
        <section className="conditions-grid-section">
          <div className="conditions-card-grid">
            {filteredConditions.map((c) => (
              <article
                key={c.id}
                className={
                  "condition-card" +
                  (selectedConditionName === c.name
                    ? " condition-card-highlight"
                    : "")
                }
                {...clickable(() => navigate(`/conditions/${c.id}`), `${c.name}: learn more`)}
                style={{ cursor: "pointer" }}
              >
                <header className="condition-card-header">
                  <h3 className="condition-card-title">{c.name}</h3>
                  <span className="condition-card-badge">{c.badge}</span>
                </header>
                {c.percentage && (
                  <p className="condition-card-percentage">
                    <PeopleIcon />
                    <span>{c.percentage}</span>
                  </p>
                )}
                <p className="condition-card-body">{c.description}</p>
              </article>
            ))}
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="conditions-bottom-cta">
          <h2 className="conditions-bottom-heading">
            Not sure which condition fits?
          </h2>
          <p className="conditions-bottom-text">
            Take the symptom assessment and we'll match you to the right one of
            our seven care pathways.
          </p>
          <div className="conditions-bottom-actions">
            <button
              className="primary-cta"
              onClick={() => navigate("/assessment")}
            >
              Take Symptom Assessment <span className="cta-arrow">→</span>
            </button>
            <button
              className="secondary-cta"
              onClick={() => navigate("/signup")}
            >
              Schedule Consultation
            </button>
          </div>
        </section>
      </main>
    </div>
  );
};

export default ConditionsPage;
